import type { RuntimeConfig } from "@/config/runtimeConfig";
import { loadRuntimeConfig } from "@/config/runtimeConfig";

const DEFAULT_SERVICE_IDS = {
  ARC_SERVICE: "ARC_SERVICE",
  PHISH_SERVICE: "PHISH_SERVICE",
  PHISHING_SIMULATION: "PHISHING_SIMULATION",
  SAT_SERVICE: "SAT_SERVICE",
  WEBFILTERING_SERVICE: "WEBFILTERING_SERVICE",
  SEG_SERVICE: "SEG_SERVICE",
};

export let ARC_SERVICE = DEFAULT_SERVICE_IDS.ARC_SERVICE;
export let PHISH_SERVICE = DEFAULT_SERVICE_IDS.PHISH_SERVICE;
export let PHISHING_SIMULATION = DEFAULT_SERVICE_IDS.PHISHING_SIMULATION;
export let SAT_SERVICE = DEFAULT_SERVICE_IDS.SAT_SERVICE;
export let WEBFILTERING_SERVICE = DEFAULT_SERVICE_IDS.WEBFILTERING_SERVICE;
export let SEG_SERVICE = DEFAULT_SERVICE_IDS.SEG_SERVICE;

export type Solution =
  | typeof ARC_SERVICE
  | typeof PHISH_SERVICE
  | typeof PHISHING_SIMULATION
  | typeof SAT_SERVICE
  | typeof WEBFILTERING_SERVICE;

export const SERVICE_ICON = Object.freeze({
  ARC_SERVICE: "cb-icon-email-archiving",
  PHISH_SERVICE: "cb-icon-email",
  PHISHING_SIMULATION: "cb-icon-phishing",
  SAT_SERVICE: "cb-icon-training",
  WEBFILTERING_SERVICE: "cb-icon-domain",
});

export interface AllServicesObject {
  uuid: string;
  menuItemLabel: string;
  tooltipLabel: string;
  tooltipLabelUnavailable: string;
  icon: string;
  customerDefaultRouteName: string;
  isServiceEnabled?: boolean;
  mspMenuItemTooltipUnavailable?: string;
}

const buildAllServices = () =>
  Object.freeze([
    {
      uuid: PHISH_SERVICE,
      menuItemLabel: "email-security-solution",
      tooltipLabel: "email-security-solution",
      tooltipLabelUnavailable: "no-email-security-license",
      icon: SERVICE_ICON.PHISH_SERVICE,
      customerDefaultRouteName: "customer-email_security",
      mspMenuItemTooltipUnavailable: "no-email-security-yet",
    },
    {
      uuid: PHISHING_SIMULATION,
      menuItemLabel: "phishing-simulation-label",
      tooltipLabel: "phishing-simulation-label",
      tooltipLabelUnavailable: "no-phs-license",
      icon: SERVICE_ICON.PHISHING_SIMULATION,
      customerDefaultRouteName: "customer-phishing_simulation",
      mspMenuItemTooltipUnavailable: "no-phs-yet",
    },
    {
      uuid: SAT_SERVICE,
      menuItemLabel: "sat-label",
      tooltipLabel: "sat-label",
      tooltipLabelUnavailable: "no-sat-license",
      icon: SERVICE_ICON.SAT_SERVICE,
      customerDefaultRouteName: "customer-security_awareness_training",
      mspMenuItemTooltipUnavailable: "no-sat-yet",
    },
    {
      uuid: ARC_SERVICE,
      menuItemLabel: "email-archiving-label",
      tooltipLabel: "email-archiving-label",
      tooltipLabelUnavailable: "no-email-archiving-license",
      icon: SERVICE_ICON.ARC_SERVICE,
      customerDefaultRouteName: "customer-arc_titan",
      mspMenuItemTooltipUnavailable: "no-email-archiving-yet",
    },
  ]);

export let ALL_SERVICES: Readonly<AllServicesObject[]> = buildAllServices();

export const configureServicesFromRuntimeConfig = (config: RuntimeConfig) => {
  ARC_SERVICE = config.VITE_ARC_SERVICE || ARC_SERVICE;
  PHISH_SERVICE = config.VITE_PHISH_SERVICE || PHISH_SERVICE;
  PHISHING_SIMULATION =
    config.VITE_PHISING_SIMULATION_SERVICE || PHISHING_SIMULATION;
  SAT_SERVICE = config.VITE_SAT_SERVICE || SAT_SERVICE;
  WEBFILTERING_SERVICE = config.VITE_PHISH_SERVICE || WEBFILTERING_SERVICE;
  SEG_SERVICE = config.VITE_SEG_SERVICE || SEG_SERVICE;
  ALL_SERVICES = buildAllServices();
};

export const resetServicesForTests = () => {
  ARC_SERVICE = DEFAULT_SERVICE_IDS.ARC_SERVICE;
  PHISH_SERVICE = DEFAULT_SERVICE_IDS.PHISH_SERVICE;
  PHISHING_SIMULATION = DEFAULT_SERVICE_IDS.PHISHING_SIMULATION;
  SAT_SERVICE = DEFAULT_SERVICE_IDS.SAT_SERVICE;
  WEBFILTERING_SERVICE = DEFAULT_SERVICE_IDS.WEBFILTERING_SERVICE;
  SEG_SERVICE = DEFAULT_SERVICE_IDS.SEG_SERVICE;
  ALL_SERVICES = buildAllServices();
};

const tryConfigureServicesFromRuntimeConfig = () => {
  try {
    configureServicesFromRuntimeConfig(loadRuntimeConfig());
  } catch (_error) {
    // Runtime config may be unavailable in tests or build-time tooling.
  }
};

tryConfigureServicesFromRuntimeConfig();
